import { useState } from 'react'
import { ChevronDown, RefreshCw, Settings2Icon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { VisualScreen } from 'app/frontend/types/custom/VisualPlan'
import { cn } from '@/lib/utils'
import Card from 'app/frontend/base/components/Card'
import VisualCanvas from './visual_plans/VisualCanvas'
import { ResolvedEntityBadge } from './visual_plans/shapes/entityAssignmentMatching'
import RaidstratsContainer, { RaidstratsPlayer } from './RaidstratsContainer'
import {
  AssignmentPhaseTimestampBadge,
  AssignmentPhaseTimestampBadgeData,
} from './AssignmentPhaseTimestampBadge'
import { EncounterAssignmentEntityOverride } from '../../TeamEvent/components/eventActivityPlanOverrides'
import { ResolvedAssignmentParticipant } from '../../TeamEvent/components/eventActivityTimelineResolver'

export type VisualRaidPlan = { raidPlanId: string; players: RaidstratsPlayer[] }

export type VisualPlanScreen = {
  key: string
  screen: VisualScreen
  number: number
  entityAssignments: Map<number, ResolvedEntityBadge>
  castTimestamps: AssignmentPhaseTimestampBadgeData[]
  scopeId?: string
}

export type VisualPlanData = {
  encounterId: number
  screens: VisualPlanScreen[]
}

type Props = {
  label: string
  visualPlan: VisualPlanData | null
  visual: VisualRaidPlan | null
  defaultExpanded?: boolean
  asCard?: boolean
  hideHeader?: boolean
  canEditEntities?: boolean
  canEditUnassignedSlots?: boolean
  selectedParticipants?: ResolvedAssignmentParticipant[]
  onEntityOverrideSelect?: (
    slotKey: string,
    override: EncounterAssignmentEntityOverride,
  ) => void
  onResetOverrides?: () => void
}

const TeamPlanAssignmentVisualCard = ({
  label,
  visualPlan,
  visual,
  defaultExpanded = false,
  asCard = true,
  hideHeader = false,
  canEditEntities = false,
  canEditUnassignedSlots = true,
  selectedParticipants,
  onEntityOverrideSelect,
  onResetOverrides,
}: Props) => {
  const [showVisual, setShowVisual] = useState(defaultExpanded || hideHeader)
  const [raidPlanReloadKey, setRaidPlanReloadKey] = useState(0)
  const [activeScreenKey, setActiveScreenKey] = useState<string>()
  const screens = visualPlan?.screens ?? []
  const ownScreen = screens.find((entry) =>
    Array.from(entry.entityAssignments.values()).some((badge) => badge.isOwn),
  )
  const activeScreen =
    screens.find((entry) => entry.key === activeScreenKey) ??
    ownScreen ??
    screens[0]
  const singleCastPerScreen =
    screens.length > 0 &&
    screens.every((entry) => entry.castTimestamps.length === 1)
  const everyScreenHasCast =
    screens.length > 0 &&
    screens.every((entry) => entry.castTimestamps.length >= 1)
  const showCastBadges = !singleCastPerScreen && everyScreenHasCast
  const showOverlayRow = screens.length > 1 || Boolean(onResetOverrides)

  const content = (
    <>
      {hideHeader ? null : (
        <div className='flex items-center gap-2'>
          <button
            type='button'
            onClick={() => setShowVisual((value) => !value)}
            className={cn(
              'flex grow items-center gap-1.5 text-sm font-medium',
              asCard ? 'px-3 pt-2' : 'px-4 pt-3',
              showVisual ? (asCard ? 'pb-0' : '') : asCard ? 'pb-2' : 'pb-3',
            )}
          >
            <ChevronDown
              className={cn(
                'h-4 w-4 transition-transform',
                showVisual ? '' : '-rotate-90',
              )}
            />
            <span>{label}</span>
            <span className='ml-auto text-xs font-normal text-muted-foreground'>
              {showVisual ? 'Click to hide' : 'Click to show'}
            </span>
          </button>
          {showVisual && visual ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type='button'
                  variant='outline'
                  size='icon'
                  className={cn('h-7 w-7', asCard ? 'mr-3' : 'mr-4')}
                  onClick={() => setRaidPlanReloadKey((value) => value + 1)}
                >
                  <RefreshCw className='h-3.5 w-3.5' />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Fetch latest raid plan version</TooltipContent>
            </Tooltip>
          ) : null}
        </div>
      )}
      {showVisual ? (
        <div
          className={cn(
            'space-y-2 overflow-hidden rounded-b-md',
            hideHeader ? '' : 'mt-2',
          )}
        >
          {visualPlan && activeScreen ? (
            <div className='relative'>
              <VisualCanvas
                encounterId={visualPlan.encounterId}
                imageIndex={activeScreen.screen.image_index}
                shapes={activeScreen.screen.shapes}
                zoom={activeScreen.screen.zoom}
                entityAssignments={activeScreen.entityAssignments}
                className='w-full overflow-hidden'
                screenId={activeScreen.screen.id}
                canEditEntities={canEditEntities}
                canEditUnassignedSlots={canEditUnassignedSlots}
                selectedParticipants={selectedParticipants}
                onEntityOverrideSelect={onEntityOverrideSelect}
              />
              {showOverlayRow ? (
                <div className='pointer-events-none absolute inset-x-0 top-0 z-10 flex flex-col items-center gap-1 p-2'>
                  <div className='pointer-events-auto flex items-center gap-1'>
                    {screens.length > 1 ? (
                      <Tabs
                        value={activeScreen.key}
                        onValueChange={setActiveScreenKey}
                      >
                        <TabsList className='bg-card/70 transition-colors hover:bg-card/90'>
                          {screens.map((entry) => (
                            <TabsTrigger key={entry.key} value={entry.key}>
                              {singleCastPerScreen
                                ? entry.castTimestamps[0].timeLabel
                                : entry.number}
                            </TabsTrigger>
                          ))}
                        </TabsList>
                      </Tabs>
                    ) : null}
                    {onResetOverrides ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant='outline'
                            size='xs'
                            className='bg-card/70'
                            aria-label='Visual override actions'
                          >
                            <Settings2Icon className='h-3.5 w-3.5' />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align='end'>
                          <DropdownMenuItem onSelect={() => onResetOverrides()}>
                            Reset changes
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                  </div>
                  {showCastBadges && activeScreen.castTimestamps.length > 0 ? (
                    <div className='hidden flex-wrap justify-center gap-1 sm:flex'>
                      {activeScreen.castTimestamps.map((badge, index) => (
                        <AssignmentPhaseTimestampBadge key={index} {...badge} />
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
          {visual ? (
            <RaidstratsContainer
              key={raidPlanReloadKey}
              raidPlanId={visual.raidPlanId}
              players={visual.players}
            />
          ) : null}
        </div>
      ) : null}
    </>
  )

  if (!asCard) return content
  return <Card className='p-0'>{content}</Card>
}

export default TeamPlanAssignmentVisualCard
